import type { UIMessage } from "ai";

/** Attachment rules for the Documentation help chat, shared by the browser and the route. */

export const HELP_CHAT_ATTACHMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  // The composer turns a very long paste into a text file.
  "text/plain",
] as const;

export const HELP_CHAT_MAX_ATTACHMENTS = 4;

// Images are re-sent with every turn. Only the most recent user turns keep
// theirs so requests stay well under the hosting body-size limit.
const USER_TURNS_WITH_ATTACHMENTS = 2;

const ALLOWED_TYPES = new Set<string>(HELP_CHAT_ATTACHMENT_TYPES);

/** Drop file parts from all but the latest user turns, leaving a note in their place. */
export function withRecentAttachmentsOnly(messages: UIMessage[]): UIMessage[] {
  let userTurnsSeen = 0;
  return [...messages]
    .reverse()
    .map((message) => {
      if (message.role !== "user") return message;
      userTurnsSeen += 1;
      if (userTurnsSeen <= USER_TURNS_WITH_ATTACHMENTS) return message;
      const fileCount = message.parts.filter((part) => part.type === "file").length;
      if (fileCount === 0) return message;
      return {
        ...message,
        parts: [
          ...message.parts.filter((part) => part.type !== "file"),
          { type: "text" as const, text: `[${fileCount} earlier attachment${fileCount === 1 ? "" : "s"} not re-sent]` },
        ],
      };
    })
    .reverse();
}

/**
 * Keep only inline (data: URL) images and pasted text on user messages, so a
 * crafted request can't make the server fetch a remote URL or send
 * unsupported file types to the model.
 */
export function sanitizeHelpChatAttachments(messages: UIMessage[]): UIMessage[] {
  return messages.map((message) => {
    let kept = 0;
    const parts = message.parts.filter((part) => {
      if (part.type !== "file") return true;
      const ok =
        message.role === "user" &&
        ALLOWED_TYPES.has(part.mediaType) &&
        part.url.startsWith(`data:${part.mediaType};`) &&
        kept < HELP_CHAT_MAX_ATTACHMENTS;
      if (ok) kept += 1;
      return ok;
    });
    return parts.length === message.parts.length ? message : { ...message, parts };
  });
}
